import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, FONTS } from '../constants/theme';

interface Particle {
  graphics: Graphics;
  vx: number;
  vy: number;
  life: number;
}

/**
 * Visual effects system for PixiJS
 * Replaces DOM-based VisualEffects
 */
export class PixiEffects {
  public readonly container: Container;
  private particles: Particle[] = [];

  constructor() {
    this.container = new Container();
  }

  /**
   * Update particles each frame
   */
  update(delta: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.graphics.x += p.vx * delta;
      p.graphics.y += p.vy * delta;
      p.vy += 0.5 * delta; // gravity
      p.life -= delta;
      p.graphics.alpha = Math.max(0, p.life / 30);

      if (p.life <= 0) {
        p.graphics.destroy();
        this.particles.splice(i, 1);
      }
    }
  }

  /**
   * Play defeat explosion effect
   */
  playDefeatEffect(x: number, y: number): void {
    // Create multiple waves of particles for more impact
    const particleCount = 16;

    // First wave - fast outward burst
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2 + Math.random() * 0.3;
      const speed = 4 + Math.random() * 3;

      const graphics = new Graphics();
      const size = 4 + Math.random() * 6;
      graphics.circle(0, 0, size);
      graphics.fill({ color: Math.random() > 0.5 ? COLORS.DANGER : COLORS.WARNING });
      graphics.x = x;
      graphics.y = y;

      this.container.addChild(graphics);

      const particle: Particle = {
        graphics,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 3,
        life: 40,
      };

      this.particles.push(particle);
    }

    // Second wave - slower, larger particles (delayed slightly via position offset)
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const speed = 2 + Math.random() * 2;

      const graphics = new Graphics();
      const size = 6 + Math.random() * 8;
      graphics.circle(0, 0, size);
      graphics.fill({ color: 0xffaa00, alpha: 0.8 });
      graphics.x = x;
      graphics.y = y;

      this.container.addChild(graphics);

      const particle: Particle = {
        graphics,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 1,
        life: 50,
      };

      this.particles.push(particle);
    }

    // Shockwave ring effect
    const ring = new Graphics();
    ring.circle(0, 0, 10);
    ring.stroke({ color: COLORS.WARNING, width: 4, alpha: 0.8 });
    ring.x = x;
    ring.y = y;
    this.container.addChild(ring);

    gsap.to(ring.scale, {
      x: 6,
      y: 6,
      duration: 0.4,
      ease: 'power2.out',
    });

    gsap.to(ring, {
      alpha: 0,
      duration: 0.4,
      onComplete: () => ring.destroy(),
    });

    // Screen flash effect
    const flash = new Graphics();
    flash.rect(0, 0, window.innerWidth, window.innerHeight);
    flash.fill({ color: COLORS.DANGER, alpha: 0.25 });
    this.container.addChild(flash);

    gsap.to(flash, {
      alpha: 0,
      duration: 0.3,
      onComplete: () => flash.destroy(),
    });
  }

  /**
   * Play floating damage number
   */
  playDamageNumber(x: number, y: number, damage: number): void {
    const style = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.LG,
      fontWeight: 'bold',
      fill: COLORS.DANGER,
      stroke: { color: 0x000000, width: 3 },
    });

    const text = new Text({ text: `-${damage}`, style });
    text.anchor.set(0.5);
    text.x = x;
    text.y = y;

    this.container.addChild(text);

    gsap.to(text, {
      y: y - 50,
      alpha: 0,
      duration: 1,
      ease: 'power2.out',
      onComplete: () => text.destroy(),
    });
  }

  /**
   * Play floating heal number
   */
  playHealNumber(x: number, y: number, amount: number): void {
    const style = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.LG,
      fontWeight: 'bold',
      fill: COLORS.SUCCESS,
      stroke: { color: 0x000000, width: 3 },
    });

    const text = new Text({ text: `+${amount}`, style });
    text.anchor.set(0.5);
    text.x = x;
    text.y = y;

    this.container.addChild(text);

    gsap.to(text, {
      y: y - 50,
      alpha: 0,
      duration: 1,
      ease: 'power2.out',
      onComplete: () => text.destroy(),
    });
  }

  /**
   * Play attack lightning effect from attacker to target
   */
  playAttackLightning(fromX: number, fromY: number, toX: number, toY: number): void {
    this.playLightningBolt(fromX, fromY, toX, toY, COLORS.DANGER, 0xffaa00);
  }

  /**
   * Play heal lightning effect from healer to target
   */
  playHealLightning(fromX: number, fromY: number, toX: number, toY: number): void {
    this.playLightningBolt(fromX, fromY, toX, toY, COLORS.SUCCESS, 0x90ff90);
  }

  /**
   * Draw animated lightning bolt between two points
   */
  private playLightningBolt(
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    mainColor: number,
    glowColor: number
  ): void {
    const lightning = new Graphics();
    this.container.addChild(lightning);

    // Calculate direction
    const dx = toX - fromX;
    const dy = toY - fromY;
    const distance = Math.hypot(dx, dy);
    const segments = Math.max(4, Math.floor(distance / 20));

    // Generate jagged path
    const points: { x: number; y: number }[] = [{ x: fromX, y: fromY }];

    for (let i = 1; i < segments; i++) {
      const t = i / segments;
      const baseX = fromX + dx * t;
      const baseY = fromY + dy * t;

      // Perpendicular offset for jaggedness
      const perpX = -dy / distance;
      const perpY = dx / distance;
      const jag = (Math.random() - 0.5) * 25 * (1 - Math.abs(t - 0.5) * 2); // More jag in middle

      points.push({
        x: baseX + perpX * jag,
        y: baseY + perpY * jag,
      });
    }

    points.push({ x: toX, y: toY });

    // Draw glow layer (wider, semi-transparent)
    lightning.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      lightning.lineTo(points[i].x, points[i].y);
    }
    lightning.stroke({ color: glowColor, width: 8, alpha: 0.4 });

    // Draw main lightning
    lightning.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      lightning.lineTo(points[i].x, points[i].y);
    }
    lightning.stroke({ color: mainColor, width: 3, alpha: 0.9 });

    // Draw bright core
    lightning.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      lightning.lineTo(points[i].x, points[i].y);
    }
    lightning.stroke({ color: 0xffffff, width: 1.5, alpha: 1 });

    // Add impact flash at target
    const impact = new Graphics();
    impact.circle(toX, toY, 15);
    impact.fill({ color: mainColor, alpha: 0.7 });
    this.container.addChild(impact);

    gsap.to(impact.scale, {
      x: 2,
      y: 2,
      duration: 0.3,
      ease: 'power2.out',
    });

    gsap.to(impact, {
      alpha: 0,
      duration: 0.3,
      onComplete: () => impact.destroy(),
    });

    // Animate lightning: flicker and fade
    const flickerCount = 3;
    const timeline = gsap.timeline();

    for (let i = 0; i < flickerCount; i++) {
      timeline.to(lightning, {
        alpha: 0.3,
        duration: 0.05,
      });
      timeline.to(lightning, {
        alpha: 1,
        duration: 0.05,
      });
    }

    timeline.to(lightning, {
      alpha: 0,
      duration: 0.2,
      delay: 0.1,
      onComplete: () => lightning.destroy(),
    });
  }

  /**
   * Clean up all effects
   */
  dispose(): void {
    for (const p of this.particles) {
      p.graphics.destroy();
    }
    this.particles = [];
    this.container.destroy({ children: true });
  }
}
